/**
 * AlertHistoryTable Component
 *
 * Displays resolved and dismissed alerts in an IndexTable.
 * Shows historical alert data with resolution status and date.
 */

import {
  IndexTable,
  Badge,
  Text,
  InlineStack,
  BlockStack,
  Box,
} from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import type { EnhancedAlert } from '../../types/dashboard';
import { ProductCell } from './ProductCell';
import { getSeverityBadge } from './AlertsTable';

/**
 * Check if a product title indicates test data
 */
function isTestProduct(title: string): boolean {
  return /\(Test\s*\d*\)/i.test(title);
}

/**
 * Props for AlertHistoryTable component
 */
export interface AlertHistoryTableProps {
  alerts: EnhancedAlert[];
  /** Whether the shop has test data (used for test badge display) */
  hasTestData?: boolean;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format shop domain for display (remove .myshopify.com suffix)
 */
function formatShopDomain(shopDomain: string): string {
  return shopDomain.replace('.myshopify.com', '');
}

/**
 * Empty state component for when no history exists
 */
function AlertHistoryEmptyState() {
  return (
    <Box paddingBlock="400">
      <div className="empty-state-enter">
        <BlockStack gap="400" inlineAlign="center">
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'var(--p-color-bg-success-subdued)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon width={24} height={24} />
          </div>
          <BlockStack gap="200" inlineAlign="center">
            <Text as="h3" variant="headingMd" alignment="center">
              No alert history yet
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
              Resolved and dismissed alerts will appear here
            </Text>
          </BlockStack>
        </BlockStack>
      </div>
    </Box>
  );
}

/**
 * Alert History IndexTable component
 */
export function AlertHistoryTable({ alerts, hasTestData = false }: AlertHistoryTableProps) {
  if (alerts.length === 0) {
    return <AlertHistoryEmptyState />;
  }

  return (
    <BlockStack gap="300">
      <div className="animated-table">
        <IndexTable
          resourceName={{ singular: 'alert', plural: 'alerts' }}
          itemCount={alerts.length}
          headings={[
            { title: 'Product' },
            { title: 'Severity', alignment: 'center' },
            { title: 'Resolution', alignment: 'center' },
            { title: 'Created' },
            { title: 'Resolved' },
          ]}
          selectable={false}
        >
          {alerts.map((alert, index) => {
            const createdDate = formatDate(alert.created_at);
            const resolvedDate = formatDate(alert.updated_at);
            const resolvedBy = alert.resolved_by ? formatShopDomain(alert.resolved_by) : null;

            return (
              <IndexTable.Row
                id={alert.id.toString()}
                key={alert.id}
                position={index}
              >
                <IndexTable.Cell>
                  <ProductCell
                    title={alert.product_title || `Variant ${alert.variant_id}`}
                    imageUrl={alert.image_url}
                    variantTitle={alert.variant_title}
                    showTestBadge={hasTestData && isTestProduct(alert.product_title || '')}
                  />
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack align="center" blockAlign="center">
                    {getSeverityBadge(alert.severity)}
                  </InlineStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack align="center" blockAlign="center">
                    <Badge tone={alert.status === 'resolved' ? 'success' : 'info'}>
                      {alert.status === 'resolved' ? 'Resolved' : 'Dismissed'}
                    </Badge>
                  </InlineStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd" tone="subdued">
                    {createdDate}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {resolvedDate}
                    </Text>
                    {resolvedBy && (
                      <Text as="span" variant="bodySm" tone="subdued">
                        by {resolvedBy}
                      </Text>
                    )}
                  </BlockStack>
                </IndexTable.Cell>
              </IndexTable.Row>
            );
          })}
        </IndexTable>
      </div>
      <Text as="p" variant="bodySm" tone="subdued">
        Showing {alerts.length} resolved or dismissed alert{alerts.length !== 1 ? 's' : ''}
      </Text>
    </BlockStack>
  );
}
